import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useBreakpoint } from "../../../libs/breakpoint";
import './Drawer.scss';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { DisableBodyScroll } from '../../../hooks/useDisableBodyScroll';

const Drawer = ({
    open = false,
    handleClose,
    children,
    headContent,
    closeBtn = true,
    xsmall, small, large, xlarge,
    overflowEnabled = true,
    unlimitedHeight = false,
    darkMode,
    lockBackgroundClose = false,   // <— NEW : empêche la fermeture via le fond
    lockEscClose = false,          // <— (optionnel) bloque la touche Échap
}) => {
    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.xs || breakpoints.sm;

    /**
     * LA SORTIE ANIMÉE (demande de Vincent, 22/10/2026 : « rendre la fermeture plus fluide »).
     *
     * Le tiroir montait en 0,3 s puis disparaissait d'un coup : `open ? portal : null` retire le
     * nœud du DOM au rendu suivant, et une animation CSS ne peut pas jouer sur un élément qui n'est
     * plus là. On le garde donc monté le temps de la descente.
     *
     * Rouvrir pendant la descente ANNULE la sortie — sans quoi le minuteur démonterait un tiroir
     * qu'on vient de rouvrir.
     */
    const [mounted, setMounted] = useState(open);
    const [closing, setClosing] = useState(false);

    useEffect(() => {
        if (open) {
            setMounted(true);
            setClosing(false);
            return undefined;
        }
        if (!mounted) return undefined;
        setClosing(true);
        const timer = setTimeout(() => {
            setClosing(false);
            setMounted(false);
        }, 280); // 250 ms d'animation + une marge : démonter pile à la fin laisse passer une image
        return () => clearTimeout(timer);
    }, [open, mounted]);

    // (Optionnel) Empêcher ESC de fermer
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (lockEscClose) return;       // ignore ESC si verrouillé
                handleClose?.();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, lockEscClose, handleClose]);

    return mounted ? ReactDOM.createPortal(
        <div className={classNames("drawer", { 'is-closing': closing, 'modal-mode': !isMobile, xsmall, small, large, xlarge, 'overflow-enabled': overflowEnabled, 'dark-mode': darkMode, 'unlimited-height': unlimitedHeight })}>
            {/* RELÂCHÉ DÈS LE DÉBUT DE LA DESCENTE, pas à la fin. `overflow: hidden` sur le body
                est rendu au démontage ; gardé jusqu'au bout, la page derrière reprenait sa hauteur
                juste après l'arrêt du panneau — un sursaut bien visible. Relâché pendant que le
                panneau descend encore, le mouvement le couvre. */}
            {!closing && <DisableBodyScroll />}
            <div className="drawer-wrapper" >
                <div className={classNames("drawer-head", { "has-head-contrent": !!headContent })}>
                    {headContent}
                    {(closeBtn && typeof handleClose === 'function') && (
                        <div className="drawer-close-btn" onClick={handleClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </div>
                    )}
                </div>
                <div className="drawer-content">
                    {children}
                </div>
            </div>
            {/* BACKDROP */}
            <div
                className={classNames("drawer-close", { 'is-disabled': lockBackgroundClose })}
                onClick={lockBackgroundClose ? undefined : (handleClose || (() => { }))}
                aria-hidden="true"
            />
        </div>
        ,
        document.body) : null;
};

export default Drawer;
